import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { EntityUsageCard } from '../components/EntityUsageCard';
import { AdminReassignCard } from '../components/AdminReassignCard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { Modal } from '../components/ui/Modal';
import { CustomerForm } from '../components/forms/CustomerForm';
import { getCustomerById, updateCustomer } from '../api/customers';
import { getProperties } from '../api/properties';
import { stripeFetch } from '../api/stripe';
import { getDevices } from '../api/devices';
import { getUnits } from '../api/units';
import { apiCreate, apiUpdate, apiList, apiRequestGet } from '../api/apiClient';
import { PropertyForm } from '../components/forms/PropertyForm';
import { Input } from '../components/ui/Input';
import { Property, Device } from '../types';
import { formatCurrency } from '../utils/formatters';
import { STRIPE_PROXY_URL, calcDeviceCost } from '../config/billing';
import { EditStripeCustomerModal } from '../components/billing/EditStripeCustomerModal';
import { UsersList } from '../components/UsersList';
import { SubscriptionActions } from '../components/billing/SubscriptionActions';
import { SubscriptionDetailModal } from '../components/billing/SubscriptionDetailModal';
import { CustomPricingModal } from '../components/billing/CustomPricingModal';
import { RatesTab } from '../components/billing/RatesTab';
import { FeatureToggleList } from '../components/billing/FeatureToggleList';
import { useAuth } from '../hooks/useAuth';
import { isSuperAdmin } from '../utils/roles';
import { refreshAllCounters } from '../utils/refreshCounters';
import { isAdminHost } from '../utils/host';
import { normalizeDeviceConnectivity, summarizeDeviceConnectivity } from '../utils/deviceConnectivity';
import { entityIdentityCandidates, entityMatchesId, findEntityById } from '../utils/entityIdentity';
import { formatDeviceType } from '../utils/presentation';

const statusColor = (s: string) => {
  switch (s) {
    case 'active': case 'paid': return 'green';
    case 'trialing': return 'blue';
    case 'past_due': case 'open': return 'yellow';
    case 'canceled': case 'incomplete_expired': case 'void': return 'red';
    default: return 'gray';
  }
};

const fmtDate = (ts: number) => ts ? new Date(ts * 1000).toLocaleDateString() : '—';
const fmtAmount = (cents: number) => cents != null ? `$${(cents / 100).toFixed(2)}` : '—';

export const CustomerDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'customer';
  const [editModal, setEditModal] = useState(false);
  const [customer, setCustomer] = useState<any>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailTab, setDetailTab] = useState<'overview' | 'customer' | 'billing' | 'contacts' | 'properties' | 'users'>('overview');
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [editStripeOpen, setEditStripeOpen] = useState(false);
  const [customPricingOpen, setCustomPricingOpen] = useState(false);
  const [detailSub, setDetailSub] = useState<any>(null);
  const [createPropertyOpen, setCreatePropertyOpen] = useState(false);
  const [assignPropertyOpen, setAssignPropertyOpen] = useState(false);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [assignPropertyId, setAssignPropertyId] = useState('');

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [custData, propData, devData, unitData] = await Promise.all([
          getCustomerById(id!),
          getProperties().catch(() => []),
          getDevices().catch(() => []),
          getUnits().catch(() => []),
        ]);
        setCustomer({ ...custData, company: custData.company || custData.company_name || custData.name });
        const myProps = (propData || []).filter((p: any) => String(p.customer_id) === String(id) || p.customer_uuid === id);
        const propIds = new Set(myProps.flatMap((property: any) => entityIdentityCandidates(property)));

        // Compute unit/device counts per property
        const unitCounts = new Map<number, number>();
        (unitData || []).forEach((u: any) => { if (u.property_id && propIds.has(String(u.property_id))) unitCounts.set(u.property_id, (unitCounts.get(u.property_id) || 0) + 1); });
        const deviceCounts = new Map<number, number>();
        (devData || []).forEach((d: any) => { if (d.property_id && propIds.has(String(d.property_id))) deviceCounts.set(d.property_id, (deviceCounts.get(d.property_id) || 0) + 1); });

        const liveRows = (await Promise.all(myProps.map(async (property: any) => {
          const propertyId = String(property.uuid || property.id || '');
          if (!propertyId) return [];
          try {
            const rows = await apiRequestGet(`/devices/live?property_id=${encodeURIComponent(propertyId)}`);
            return Array.isArray(rows) ? rows : [];
          } catch {
            return [];
          }
        }))).flat();
        const liveById: Record<string, any> = {};
        liveRows.forEach((row: any) => {
          for (const key of [row.id, row.uuid].filter(Boolean)) liveById[String(key)] = row;
        });

        setProperties(myProps.map((p: any) => {
          const uc = p.units_qty ?? p.unit_count ?? unitCounts.get(p.id) ?? 0;
          const dc = p.devices_qty ?? p.device_count ?? deviceCounts.get(p.id) ?? 0;
          const pc = p.telnyx_number ? 1 : 0;
          const propDevs = (devData || []).filter((d: any) => entityMatchesId(p, d.property_id));
          return { ...p, unit_count: uc, device_count: dc, phone_count: pc, monthly: (uc * 2) + calcDeviceCost(propDevs) + (pc * 3) };
        }));
        setDevices((devData || []).filter((d: any) => propIds.has(String(d.property_id))).map((d: any) => {
          const live = liveById[String(d.uuid || d.id)] || null;
          return {
            ...d,
            name: d.name || d.device_name || 'Unnamed Device',
            type: formatDeviceType(d.type || d.device_type, 'Unknown'),
            property_name: findEntityById(myProps, d.property_id)?.name || '',
            _connectivity: normalizeDeviceConnectivity(d, live),
          };
        }));

        // Fetch billing data if stripe customer exists
        if (custData.stripe_customer_id) {
          setBillingLoading(true);
          const [subsRes, invRes] = await Promise.all([
            stripeFetch(`${STRIPE_PROXY_URL}/subscriptions?customer=${custData.stripe_customer_id}`).then(r => r.json()).catch(() => []),
            stripeFetch(`${STRIPE_PROXY_URL}/invoices?customer=${custData.stripe_customer_id}`).then(r => r.json()).catch(() => []),
          ]);
          setSubscriptions(Array.isArray(subsRes) ? subsRes : subsRes?.data || []);
          setInvoices(Array.isArray(invRes) ? invRes : invRes?.data || []);
          setBillingLoading(false);
        }
      } catch (e) {
        console.error('Failed to load customer:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleUpdate = async (data: any) => {
    const saved = await updateCustomer(id!, data);
    refreshAllCounters();
    setCustomer((prev: any) => ({
      ...prev,
      ...data,
      ...(saved || {}),
      company: saved?.company || saved?.company_name || saved?.name || data.name || data.company_name || prev.company,
    }));
    setEditModal(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  if (!customer) return <div className="text-center py-12 text-gray-400">Customer not found</div>;

  const connectivity = summarizeDeviceConnectivity(devices, Object.fromEntries(devices.map((device: any) => [String(device.uuid || device.id), {
    ...device,
    online: device._connectivity?.online,
    connection_status: device._connectivity?.state,
    last_seen: device._connectivity?.observedAt,
  }])));
  const onlineDevices = connectivity.online;

  return (
    <div className="shadow-detail-page customer-detail-page">
      <button onClick={() => navigate('/customers')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>Back to Customers
      </button>
      <header className="prototype-entity-hero"><div><small>Intrlock app / customer</small><h1>{customer.company}</h1><p>Customer profile, portfolio access, users, systems, and billing configuration.</p></div><div><Badge status={customer.status || 'active'}>{customer.status || 'active'}</Badge><Button variant="secondary" onClick={() => setEditModal(true)}>Edit customer</Button></div></header>

      {/* Operations Quick View — live counters + usage scoped to this customer */}
      <div className={`mb-6 ${detailTab === 'overview' ? '' : 'hidden'}`}>
        <EntityUsageCard entityType="customer" entityId={(customer as any)?.uuid || (customer as any)?.id || id} />
      </div>
      <div className={`mb-6 ${detailTab === 'overview' ? '' : 'hidden'}`}>
        <AdminReassignCard
          entityType="customer"
          entityId={(customer as any)?.uuid || (customer as any)?.id || id || ''}
          currentDealerId={(customer as any)?.dealer_id || (customer as any)?.integrator_id}
          onSaved={() => window.location.reload()}
        />
      </div>

      {/* ══════════ Stats Grid ══════════ */}
      <div className="prototype-detail-metrics grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Properties</p>
          <p className="text-xl font-bold text-gray-900">{customer.total_properties || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Units</p>
          <p className="text-xl font-bold text-gray-900">{customer.total_units || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Devices</p>
          <p className="text-xl font-bold text-gray-900">{customer.total_devices || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Tenants</p>
          <p className="text-xl font-bold text-gray-900">{customer.total_tenants || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Guests</p>
          <p className="text-xl font-bold text-gray-900">{customer.total_guests || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Phones</p>
          <p className="text-xl font-bold text-gray-900">{customer.total_phones || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Systems</p>
          <p className="text-xl font-bold text-gray-900">{customer.total_systems || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Intercoms</p>
          <p className="text-xl font-bold text-emerald-600">{(customer as any).total_intercoms || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Mobile Apps</p>
          <p className="text-xl font-bold text-gray-900">{(customer as any).total_active_activations || 0}</p>
        </div>
      </div>
      <nav className="person-tabs customer-detail-tabs" aria-label="Customer detail sections">
        <button className={detailTab === 'overview' ? 'active' : ''} onClick={() => setDetailTab('overview')}>Overview</button>
        <button className={detailTab === 'customer' ? 'active' : ''} onClick={() => setDetailTab('customer')}>Customer Info</button>
        <button className={detailTab === 'billing' ? 'active' : ''} onClick={() => setDetailTab('billing')}>Billing Info</button>
        <button className={detailTab === 'contacts' ? 'active' : ''} onClick={() => setDetailTab('contacts')}>Contacts</button>
        <button className={detailTab === 'properties' ? 'active' : ''} onClick={() => setDetailTab('properties')}>Properties <b>{properties.length}</b></button>
        <button className={detailTab === 'users' ? 'active' : ''} onClick={() => setDetailTab('users')}>Users</button>
      </nav>

      <div className="customer-tab-layout grid grid-cols-1 gap-6">
        <div className={`customer-overview-panel space-y-6 ${detailTab === 'overview' ? '' : 'hidden'}`}>
          <Card>
            <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-semibold">{customer.company}</h2><Badge status={customer.status || 'active'}>{customer.status || 'active'}</Badge></div>
            <div className="space-y-3 text-sm">
              <div><span className="text-gray-500">Contact:</span> <span className="text-gray-900">{customer.name || '—'}</span></div>
              <div><span className="text-gray-500">Email:</span> <span className="text-gray-900">{customer.email || customer.billing_email || '—'}</span></div>
              <div><span className="text-gray-500">Phone:</span> <span className="text-gray-900">{customer.phone || customer.billing_phone || '—'}</span></div>
              <div><span className="text-gray-500">Properties:</span> <span className="font-medium text-gray-900">{properties.length}</span></div>
              <div><span className="text-gray-500">Units:</span> <span className="font-medium text-gray-900">{properties.reduce((s: number, p: any) => s + (p.unit_count || 0), 0)}</span></div>
              <div><span className="text-gray-500">Devices:</span> <span className="font-medium text-gray-900">{onlineDevices} online · {connectivity.offline} offline · {connectivity.unknown + connectivity.stale} needs check</span></div>
              <div><span className="text-gray-500">Phone Numbers:</span> <span className="font-medium text-gray-900">{properties.filter((p: any) => p.phone_count).length}</span></div>
            </div>
          </Card>
        </div>
        <div className="customer-detail-sections space-y-6">
          <Card className={detailTab === 'customer' ? '' : 'hidden'}>
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-3"><h3 className="font-semibold text-gray-900">Customer Information</h3><Button size="sm" variant="secondary" onClick={() => setEditModal(true)}>Edit</Button></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 text-sm">
              <div className="py-3 border-b border-gray-200"><span className="text-gray-500">Customer name</span><p className="font-semibold text-gray-900">{customer.company || '—'}</p></div>
              <div className="py-3 border-b border-gray-200"><span className="text-gray-500">Status</span><p><Badge status={customer.status || 'active'}>{customer.status || 'active'}</Badge></p></div>
              <div className="py-3 border-b border-gray-200 md:col-span-2"><span className="text-gray-500">Address</span><p className="font-semibold text-gray-900">{[customer.address, customer.city, customer.state, customer.zip].filter(Boolean).join(', ') || '—'}</p></div>
            </div>
          </Card>

          <Card className={detailTab === 'contacts' ? '' : 'hidden'}>
            <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-3"><h3 className="font-semibold text-gray-900">Contacts</h3><Button size="sm" variant="secondary" onClick={() => setEditModal(true)}>Edit</Button></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 text-sm">
              <div className="py-3 border-b border-gray-200"><span className="text-gray-500">Primary contact</span><p className="font-semibold text-gray-900">{customer.contact_name || [customer.contact_first_name, customer.contact_last_name].filter(Boolean).join(' ') || customer.billing_contact_name || '—'}</p></div>
              <div className="py-3 border-b border-gray-200"><span className="text-gray-500">Email</span><p className="font-semibold text-gray-900">{customer.email || customer.billing_email || '—'}</p></div>
              <div className="py-3 border-b border-gray-200"><span className="text-gray-500">Phone</span><p className="font-semibold text-gray-900">{customer.phone || customer.billing_phone || '—'}</p></div>
            </div>
          </Card>

          <Card className={detailTab === 'users' ? '' : 'hidden'}>
            <div className="border-b border-gray-100 pb-3 mb-1"><h3 className="font-semibold text-gray-900">Users</h3></div>
            <UsersList filterType="customer" filterId={id!} />
          </Card>

          <Card padding={false} className={detailTab === 'properties' ? '' : 'hidden'}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Properties ({properties.length})</h3>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={async () => { const all = await apiList('properties'); setAllProperties((all || []).filter((p: any) => !p.customer_id || p.customer_id === '0' || p.customer_id === 0 || p.customer_id === 'NONE')); setAssignPropertyOpen(true); }}>Assign Existing</Button>
                <Button size="sm" onClick={() => setCreatePropertyOpen(true)}>Add Property</Button>
              </div>
            </div>
            <Table columns={[
              { key: 'name', header: 'Name', sticky: 'primary' as const, render: (p: any) => <Link onClick={(e) => e.stopPropagation()} to={`/properties/${p.uuid || p.id}`} className="font-medium text-brand-700 hover:underline">{p.name}</Link> },
              { key: 'address', header: 'Address', render: (p: any) => `${p.address || ''}, ${p.city || ''}` },
              { key: 'unit_count', header: 'Units' },
              { key: 'device_count', header: 'Devices' },
              { key: 'phone_count', header: 'Phones' },
              // Monthly $ figure is admin-only. Spread the column conditionally
              // via spread+filter so we don't have to render a placeholder cell.
              ...(isAdminHost() ? [{ key: 'monthly', header: 'Monthly', render: (p: any) => <span className="text-green-600">${p.monthly}</span> }] : []),
              { key: 'status', header: 'Status', render: (p: any) => <Badge status={p.status}>{p.status}</Badge> },
              { key: '_actions', header: '', sticky: 'actions' as const, render: (p: any) => <Link onClick={(e) => e.stopPropagation()} to={`/properties/${p.uuid || p.id}`} className="row-open">Open <span aria-hidden="true">→</span></Link> },
            ]} data={properties} onRowClick={(p) => navigate(`/properties/${(p as any).uuid || p.id}`)} emptyMessage="No properties yet" />
          </Card>
          {devices.length > 0 && (
            <Card padding={false} className={detailTab === 'properties' ? '' : 'hidden'}>
              <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Devices ({devices.length})</h3></div>
              <Table columns={[
                { key: 'name', header: 'Name', render: (d: any) => <span className="font-medium">{d.name}</span> },
                { key: 'type', header: 'Type' },
                { key: 'property_name', header: 'Property' },
                { key: 'status', header: 'Status', render: (d: any) => <Badge status={d.status === 'online' ? 'online' : d.status === 'offline' ? 'offline' : 'error'}>{d.status}</Badge> },
              ]} data={devices} emptyMessage="No devices" />
            </Card>
          )}

          {/* Subscriptions */}
          <Card padding={false} className={detailTab === 'billing' ? '' : 'hidden'}>
            <div className="p-4 border-b border-gray-100 flex items-center justify-between"><div><h3 className="font-semibold text-gray-900">Billing Information</h3><p className="text-sm text-gray-500 mt-1">{customer.billing_contact_name || 'No billing contact'} · {customer.billing_email || 'No billing email'} · {customer.billing_phone || 'No billing phone'}</p><p className="text-sm text-gray-500">{customer.billing_address || 'No billing address'}</p></div><Button size="sm" variant="secondary" onClick={() => setEditModal(true)}>Edit</Button></div>
            <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Subscriptions</h3></div>
            {!customer.stripe_customer_id ? (
              <div className="p-4 text-gray-400 text-sm">No billing account linked</div>
            ) : billingLoading ? (
              <div className="p-4 text-gray-400 text-sm">Loading...</div>
            ) : subscriptions.length === 0 ? (
              <div className="p-4 text-gray-400 text-sm">No subscriptions</div>
            ) : (
              <Table columns={[
                { key: 'id', header: 'Subscription', render: (s: any) => <span className="font-mono text-xs">{s.id?.slice(-8)}</span> },
                { key: 'status', header: 'Status', render: (s: any) => <Badge color={statusColor(s.status)}>{s.status}</Badge> },
                { key: 'plan', header: 'Plan', render: (s: any) => {
                  const item = s.items?.data?.[0];
                  return item?.price?.product?.name || item?.price?.nickname || item?.plan?.nickname || '—';
                }},
                { key: 'quantity', header: 'Qty', render: (s: any) => s.items?.data?.[0]?.quantity || 1 },
                { key: 'period', header: 'Period', render: (s: any) => `${fmtDate(s.current_period_start)} – ${fmtDate(s.current_period_end)}` },
                { key: 'amount', header: 'Amount', render: (s: any) => {
                  const item = s.items?.data?.[0];
                  return item ? fmtAmount((item.price?.unit_amount || 0) * (item.quantity || 1)) : '—';
                }},
                { key: 'paused', header: 'Paused', render: (s: any) => s.pause_collection ? <Badge color="yellow">Paused</Badge> : null },
                { key: 'actions', header: 'Actions', render: (s: any) => <SubscriptionActions subscription={s} onUpdated={() => window.location.reload()} /> },
              ]} data={subscriptions} onRowClick={(s: any) => setDetailSub(s)} emptyMessage="No subscriptions" />
            )}
          </Card>

          {/* Invoices */}
          <Card padding={false} className={detailTab === 'billing' ? '' : 'hidden'}>
            <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Invoices</h3></div>
            {!customer.stripe_customer_id ? (
              <div className="p-4 text-gray-400 text-sm">No billing account linked</div>
            ) : billingLoading ? (
              <div className="p-4 text-gray-400 text-sm">Loading...</div>
            ) : invoices.length === 0 ? (
              <div className="p-4 text-gray-400 text-sm">No invoices</div>
            ) : (
              <Table columns={[
                { key: 'number', header: 'Invoice', render: (i: any) => <span className="font-medium">{i.number || '—'}</span> },
                { key: 'created', header: 'Date', render: (i: any) => fmtDate(i.created) },
                { key: 'amount', header: 'Amount', render: (i: any) => fmtAmount(i.amount_due) },
                { key: 'status', header: 'Status', render: (i: any) => <Badge color={statusColor(i.status)}>{i.status}</Badge> },
                { key: 'pdf', header: '', render: (i: any) => i.invoice_pdf ? <a href={i.invoice_pdf} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline text-sm">PDF</a> : null },
              ]} data={invoices} emptyMessage="No invoices" />
            )}
          </Card>
        </div>
      </div>
      {/* Billing Rates and Feature Billing now live exclusively on
          admin.intrlock.io — keep them out of the customer-facing
          surface even for super-admin accounts. */}
      {isSuperAdmin(role)
        && (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.')) && (
        <>
          <div className={`mt-6 ${detailTab === 'billing' ? '' : 'hidden'}`}>
            <RatesTab
              scopeType="customer"
              scopeId={id!}
              entityName={customer.company || customer.company_name || customer.name || ''}
              customerId={id!}
              dealerId={customer.integrator_id}
            />
          </div>
          <div className={`mt-6 ${detailTab === 'billing' ? '' : 'hidden'}`}>
            <FeatureToggleList entityType="customer" entityId={id!} />
          </div>
        </>
      )}

      <Modal open={editModal} onClose={() => setEditModal(false)} title="Edit Customer">
        <CustomerForm initialData={customer} onSubmit={handleUpdate} onCancel={() => setEditModal(false)} />
      </Modal>
      {customer.stripe_customer_id && (
        <>
          <EditStripeCustomerModal
            open={editStripeOpen}
            onClose={() => setEditStripeOpen(false)}
            stripeCustomerId={customer.stripe_customer_id}
            onUpdated={() => window.location.reload()}
          />
          <SubscriptionDetailModal
            open={!!detailSub}
            onClose={() => setDetailSub(null)}
            subscriptionId={detailSub?.id || null}
            customerName={customer.company_name || customer.name}
          />
          <CustomPricingModal
            open={customPricingOpen}
            onClose={() => setCustomPricingOpen(false)}
            entityType="customer"
            entityId={id!}
            entityName={customer.company || customer.company_name || ''}
            stripeCustomerId={customer.stripe_customer_id}
          />
        </>
      )}
      {/* Add Property Modal */}
      <Modal open={createPropertyOpen} onClose={() => setCreatePropertyOpen(false)} title="Add Property">
        <PropertyForm
          customerId={id as any}
          onSubmit={async (data) => {
            await apiCreate('properties', { ...data, customer_id: id });
            refreshAllCounters();
            setCreatePropertyOpen(false);
            window.location.reload();
          }}
          onCancel={() => setCreatePropertyOpen(false)}
        />
      </Modal>

      {/* Assign Existing Property Modal */}
      <Modal open={assignPropertyOpen} onClose={() => setAssignPropertyOpen(false)} title="Assign Existing Property">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Select an unassigned property to assign to this customer.</p>
          {allProperties.length === 0 ? (
            <p className="text-sm text-gray-400">No unassigned properties available.</p>
          ) : (
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={assignPropertyId}
              onChange={(e) => setAssignPropertyId(e.target.value)}
            >
              <option value="">Select a property...</option>
              {allProperties.map((p: any) => (
                <option key={p.uuid || p.id} value={p.uuid || p.id}>{p.name} — {p.address || 'No address'}</option>
              ))}
            </select>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setAssignPropertyOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={!assignPropertyId} onClick={async () => {
              if (!assignPropertyId) return;
              await apiUpdate('properties', assignPropertyId, { customer_id: id });
              refreshAllCounters();
              setAssignPropertyOpen(false);
              setAssignPropertyId('');
              window.location.reload();
            }}>Assign</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
